'use client';

import { useBackgroundQuery, useMutation, useReadQuery } from '@apollo/client';
import {
  Avatar,
  Flex,
  Group,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import ChevronLeft from '@material-design-icons/svg/filled/chevron_left.svg';
import Phone from '@material-design-icons/svg/filled/phone_enabled.svg';
import VerifiedUser from '@material-design-icons/svg/outlined/verified_user.svg';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

import { LimitedTextInput, MediaUploader, TrackedButton } from '@/components';
import {
  BIO_MAX_LENGTH,
  DISPLAY_NAME_SHORT_MAX_LENGTH,
  FIRST_NAME_MAX_LENGTH,
  LAST_NAME_MAX_LENGTH,
} from '@/constants';
import {
  ProfilePhotoUpload_InsertMediaDocument as InsertMediaMutation,
  ProfilePhotoUpload_RemoveProfilePhotoDocument as RemoveProfilePhotoMutation,
  ProfilePhotoUpload_UpdateEntityProfilePhotoDocument as UpdateEntityProfilePhotoMutation,
  SettingsPage_GetEntityQueryDocument as GetEntityQuery,
  SettingsPage_UpdateEntityMutationDocument as UpdateEntityMutation,
  SettingsPage_UpdatePersonMutationDocument as UpdatePersonMutation,
} from '@/graphql';
import { useAuth } from '@/hooks';
import { logger } from '@/utils';

import styles from './EntitySettingsContent.module.css';

import '@uppy/core/dist/style.css';
import '@uppy/dashboard/dist/style.css';

export function EntitySettingsContent({
  entityId,
  placeholderBio,
}: {
  entityId: string;
  placeholderBio: string;
}) {
  const router = useRouter();
  const [displayNameShort, setDisplayNameShort] = useState('');
  const [headline, setHeadline] = useState('');
  const [phone, setPhone] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [profileImageUrl, setProfileImageUrl] = useState<string | undefined>(
    undefined,
  );

  const { isCurrentUserVerified } = useAuth();

  // UPPY FOR PROFILE PHOTO UPLOAD
  const [showUploader, setShowUploader] = useState(false);

  const [queryRef] = useBackgroundQuery(GetEntityQuery, {
    variables: { entityId },
  });

  const { data } = useReadQuery(queryRef);

  const [updateEntity] = useMutation(UpdateEntityMutation);
  const [updatePerson] = useMutation(UpdatePersonMutation);
  const [insertMedia] = useMutation(InsertMediaMutation);
  const [updateEntityProfilePhoto] = useMutation(
    UpdateEntityProfilePhotoMutation,
  );
  const [removeProfilePhoto] = useMutation(RemoveProfilePhotoMutation);

  useEffect(() => {
    if (data?.entitiesByPk) {
      const userData = data.entitiesByPk.entityPersonRels?.[0]?.person?.user;
      const personData = data.entitiesByPk.entityPersonRels?.[0]?.person;
      setFirstName(personData?.firstName || '');
      setLastName(personData?.lastName || '');
      setDisplayNameShort(data.entitiesByPk.displayNameShort || '');
      setHeadline(data.entitiesByPk.bio || '');
      setPhone(userData?.phoneNumber || '');
      setProfileImageUrl(data.entitiesByPk.profilePhoto?.link || undefined);
    }
  }, [data]);

  const toggleUploader = () => {
    setShowUploader(!showUploader);
  };

  const handleImageUpdate = (url: string[]) => {
    const urlString = url[0];
    // Insert the media first
    insertMedia({
      variables: {
        photoUrl: urlString,
      },
    }).then(({ data }) => {
      // Then update the entity's profile photo with the new media ID
      const mediaId = data?.insertMedia?.id;
      if (mediaId) {
        updateEntityProfilePhoto({
          variables: {
            entityId,
            mediaId,
          },
        }).then(() => {
          if (urlString) {
            setProfileImageUrl(urlString);
            setShowUploader(false);
          }
        });
      }
    });
  };

  const handleRemovePhoto = async () => {
    // Get the media ID before removing the profile photo
    const mediaId = data?.entitiesByPk?.profilePhoto?.id;

    // Remove the photo from the media table if we have the mediaId
    if (mediaId) {
      try {
        await removeProfilePhoto({
          variables: {
            mediaId,
          },
          refetchQueries: [{ query: GetEntityQuery, variables: { entityId } }],
        });
      } catch (error: unknown) {
        logger.error('Error removing media:', { error });
      }
    }
  };

  const handleBackButton = () => {
    router.push(`/profiles/${entityId}`);
  };

  const handlePublish = () => {
    const entityVariables = {
      entityId,
      displayNameLong: `${firstName} ${lastName}`,
      displayNameShort,
      bio: headline,
    };

    const personVariables = {
      personId: data?.entitiesByPk?.entityPersonRels?.[0]?.person?.id,
      firstName,
      lastName,
    };

    updateEntity({
      variables: entityVariables,
      refetchQueries: [{ query: GetEntityQuery, variables: { entityId } }],
    });

    updatePerson({
      variables: personVariables,
      refetchQueries: [{ query: GetEntityQuery, variables: { entityId } }],
    });

    router.push(`/profiles/${entityId}`);
  };

  return (
    <div className={styles.container}>
      <Stack
        gap="lg"
        maw={600}
        w="100%"
      >
        <div
          onClick={handleBackButton}
          className={styles.backButton}
        >
          <ChevronLeft />
          <Text style={{ paddingTop: '2px' }}>Back</Text>
        </div>
        <Stack
          gap="xl"
          mb="xl"
        >
          <Stack gap="sm">
            <Title
              order={2}
              style={{ textAlign: 'left', paddingBottom: 15 }}
            >
              Edit your profile
            </Title>
            <Flex
              gap="lg"
              align="center"
            >
              <Stack
                gap="xs"
                align="center"
                justify="center"
              >
                <Avatar
                  src={profileImageUrl}
                  className={styles.profileAvatar}
                  variant="profile"
                  alt={displayNameShort || 'Profile'}
                  style={{ cursor: 'pointer' }}
                  onClick={toggleUploader}
                />
                <Group
                  gap="xs"
                  style={{ justifyContent: 'center' }}
                >
                  <TrackedButton
                    variant="subtle"
                    size="xs"
                    onClick={toggleUploader}
                    trackingName="entity_settings_change_photo"
                  >
                    {profileImageUrl ? 'Change Photo' : 'Add Photo'}
                  </TrackedButton>
                  {profileImageUrl && (
                    <TrackedButton
                      variant="subtle"
                      color="red"
                      size="xs"
                      onClick={handleRemovePhoto}
                      trackingName="entity_settings_remove_photo"
                    >
                      Remove Photo
                    </TrackedButton>
                  )}
                </Group>
              </Stack>

              <Stack
                gap="sm"
                style={{ flexGrow: 1 }}
              >
                {showUploader && (
                  <MediaUploader
                    entityId={entityId}
                    showUploader={showUploader}
                    onImageUpdate={handleImageUpdate}
                  />
                )}
                <Group
                  gap="sm"
                  align="center"
                  grow
                >
                  <LimitedTextInput
                    value={firstName}
                    onChange={setFirstName}
                    placeholder="Enter first name"
                    label="First name"
                    maxLength={FIRST_NAME_MAX_LENGTH}
                  />
                  <LimitedTextInput
                    value={lastName}
                    onChange={setLastName}
                    placeholder="Enter last name"
                    label="Last name"
                    maxLength={LAST_NAME_MAX_LENGTH}
                  />
                </Group>
                <LimitedTextInput
                  value={displayNameShort}
                  onChange={setDisplayNameShort}
                  placeholder="This name will be used on your issue pages"
                  label="Display name"
                  maxLength={DISPLAY_NAME_SHORT_MAX_LENGTH}
                />
              </Stack>
            </Flex>

            <LimitedTextInput
              value={headline}
              onChange={setHeadline}
              placeholder={placeholderBio}
              maxLength={BIO_MAX_LENGTH}
            />
            <TextInput
              value={phone}
              disabled={true}
              variant="outline"
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Enter phone number"
              rightSection={<Phone />}
              label="Phone number"
            />
          </Stack>

          {isCurrentUserVerified ? (
            <Stack>
              <Group>
                <VerifiedUser />
                <Title order={2}>You&apos;re verified to vote</Title>
                <Text size="md">
                  By verifying your vote, you are contributing to others&apos;
                  Sway Scores.
                </Text>
              </Group>
            </Stack>
          ) : (
            <Stack>
              <Group
                justify="space-between"
                className={styles.verifyVoteGroup}
              >
                <Title order={2}>Verify your vote</Title>
                <TrackedButton
                  variant="secondary"
                  size="sm"
                  style={{ flexGrow: 1 }}
                  onClick={() => router.push('/verify')}
                  trackingName="entity_settings_verify_vote"
                  trackingProperties={{
                    entityId: entityId,
                  }}
                >
                  VERIFY
                </TrackedButton>
              </Group>
              <Text size="md">
                Verify you can vote in your community to increase your leverage.
                Sway will never store your personal voter verification data.
              </Text>
            </Stack>
          )}

          <Stack gap="md">
            <TrackedButton
              fullWidth
              onClick={handlePublish}
              variant="primary"
              size="lg"
              trackingName="entity_settings_update"
              trackingProperties={{
                entityId: entityId,
              }}
            >
              UPDATE
            </TrackedButton>
            <Text
              size="md"
              c="dimmed"
              style={{ textAlign: 'center' }}
            >
              Please contact us at help@sway.co if you would like to delete your
              account.
            </Text>
          </Stack>
        </Stack>
      </Stack>
    </div>
  );
}
